/**
 * Ce qu'un virement DIT, mis en face de ce que les factures RÉCLAMENT.
 *
 * ## Le défaut que ce module corrige
 *
 * Signalé le 18 septembre 2026, sur un virement de 180,00 € dont 70,00 €
 * restaient à placer :
 *
 *   « il est très compliqué pour l'utilisateur de comprendre ce qu'il se
 *     passe, nous avons bien les trois factures qui sont reconnues mais il y a
 *     un montant trop perçu... sans doute dû à des notes de crédit mais comme
 *     l'utilisateur ne les voit pas, il ne peut pas juger si c'est exact ou
 *     pas, s'il doit mettre hors périmètre ou mettre en provision le reste »
 *
 * Le cas réel, reconstitué à la main en interrogeant la base — ce qu'aucun
 * écran ne permettait :
 *
 * | citée             | famille    | facture | note de crédit | reste dû |
 * |-------------------|------------|--------:|---------------:|---------:|
 * | `CDV-WBK-2500766` | Kamariza   |   60,00 |         −30,00 |     0,00 |
 * | `CDV-WBK-2500769` | Cappucci   |   60,00 |              — |     0,00 |
 * | `CDV-WBK-2500770` | Cappucci   |   20,00 |              — |     0,00 |
 * | `CDV-WBK-2500771` | Cappucci   |   20,00 |              — |    20,00 |
 * | `CDV-WBK-2500772` | Cappucci   |   20,00 |              — |    20,00 |
 *
 * Trois choses rendaient l'écran illisible, et **aucune n'était un bogue** :
 *
 * 1. Deux des cinq factures citées ne l'étaient que dans le **libellé complet
 *    du relevé** — la communication bancaire s'arrête à 53 caractères. Elles
 *    n'avaient donc jamais été rapprochées, et rien ne les nommait.
 * 2. Une des cinq appartient à **une autre famille**. Le virement paie pour
 *    deux comptes, ce qu'aucun écran ne disait.
 * 3. L'excédent de 30,00 € est **exactement** la note de crédit portée par
 *    `CDV-WBK-2500766` : le parent a payé le montant d'origine.
 *
 * Les trois réponses tiennent dans une lecture. C'est ce que ce module
 * construit — sans réseau, donc éprouvable.
 *
 * ## Ce que ce module ne fait pas
 *
 * Il ne décide rien. Il ne propose même pas : il constate, il compte, et il
 * dit pourquoi les comptes ne tombent pas juste. Le geste — placer, mettre en
 * provision, écarter — reste à celui qui connaît les familles.
 */

import { allLegacyRefs } from './matching';
import type { LegacyRef } from './matching';

/** D'où vient une référence citée : les deux ne se valent pas. */
export type OrigineDeLaCitation =
  /* Le champ communication du relevé — 53 caractères, coupés au-delà. */
  | 'communication'
  /* Le libellé complet du relevé, qui porte la suite. */
  | 'releve';

export interface ReferenceCitee extends LegacyRef {
  origine: OrigineDeLaCitation;
}

/**
 * Toutes les références de facture citées par un virement, communication ET
 * libellé complet du relevé.
 *
 * La lecture se fait avec `allLegacyRefs`, **la même fonction que le
 * rapprochement automatique**. Ce n'est pas une commodité : un panneau qui
 * lirait les références autrement finirait par montrer une référence que le
 * rapprochement n'a jamais vue, ou taire celle sur laquelle il a agi. Il
 * expliquerait alors un écran qui n'existe pas.
 *
 * La communication passe en premier, et une référence déjà vue n'est pas
 * reprise : le libellé du relevé répète presque toujours la communication.
 */
export function referencesCitees(
  communication: string | null | undefined,
  libelleDuReleve: string | null | undefined,
): ReferenceCitee[] {
  const vues = new Set<string>();
  const sortie: ReferenceCitee[] = [];

  const lire = (texte: string | null | undefined, origine: OrigineDeLaCitation) => {
    if (!texte) return;
    for (const r of allLegacyRefs(texte)) {
      if (vues.has(r.digits)) continue;
      vues.add(r.digits);
      sortie.push({ ...r, origine });
    }
  };

  lire(communication, 'communication');
  lire(libelleDuReleve, 'releve');
  return sortie;
}

/**
 * Une référence citée, et la facture qu'elle désigne — quand elle en désigne
 * une.
 *
 * `invoiceId` à `null` est un résultat, pas une absence de résultat : la
 * référence a la forme d'un numéro de facture et ne correspond à aucune
 * facture de cette comptabilité. C'est précisément le signe d'un virement qui
 * paie une garderie ou un parascolaire.
 */
export interface FactureCitee {
  /** Les sept chiffres tels qu'ils ont été lus. */
  digits: string;
  origine: OrigineDeLaCitation;
  invoiceId: string | null;
  invoiceNumber: string | null;
  pdfUrl: string | null;
  userId: string | null;
  famille: string | null;
  /** L'état de la facture : `paid`, `partial`, `unpaid`, `cancelled`, `credited`. */
  settlement: string | null;
  factureCents: number;
  notesCents: number;
  netCents: number;
  /** Ce que la facture a reçu, tous virements confondus. */
  recuCents: number;
  resteCents: number;
  /** Ce que CE virement lui a déjà donné. */
  deCeVirementCents: number;
}

/** La lecture d'un virement : des nombres, et les phrases qui les expliquent. */
export interface LectureDuVirement {
  aPlacerCents: number;
  /** Ce que les factures citées réclament encore. Les annulées ne comptent pas. */
  reclameCents: number;
  /** Les notes de crédit portées par les factures citées. */
  notesCents: number;
  /**
   * Ce que les factures citées avaient déjà reçu **d'un autre virement**.
   *
   * L'autre raison, avec les notes de crédit, pour qu'une facture réclame
   * moins que son montant. Les deux se lisent pareil pour qui regarde : le
   * parent a payé le prix affiché.
   */
  dejaAilleursCents: number;
  /** `aPlacer − reclame` : positif, il reste de l'argent sans destination. */
  ecartCents: number;
  /** Les références citées qu'aucune facture ne porte. */
  inconnues: string[];
  /** Les familles concernées, dédoublonnées. Plus d'une est un avertissement. */
  familles: string[];
  /** Des phrases complètes, affichables telles quelles, dans cet ordre. */
  phrases: string[];
}

/**
 * Ce qu'une facture citée réclame réellement.
 *
 * Une facture **annulée** ne réclame rien à personne, même quand son solde
 * reste inscrit faute de note de crédit. C'est la règle déjà tenue par l'écran
 * des factures, et l'ignorer ici ferait dire à la lecture qu'il manque de
 * l'argent sur une commande que l'association a elle-même éteinte.
 */
function reclamePar(f: FactureCitee): number {
  return f.settlement === 'cancelled' ? 0 : f.resteCents;
}

export function lireLeVirement(
  aPlacerCents: number,
  factures: FactureCitee[],
): LectureDuVirement {
  const connues = factures.filter((f) => f.invoiceId !== null);
  const inconnues = factures.filter((f) => f.invoiceId === null).map((f) => f.digits);

  const reclameCents = connues.reduce((s, f) => s + reclamePar(f), 0);
  const notesCents = connues.reduce((s, f) => s + f.notesCents, 0);
  const dejaAilleursCents = connues.reduce(
    (s, f) => s + Math.max(0, f.recuCents - f.deCeVirementCents), 0,
  );
  const ecartCents = aPlacerCents - reclameCents;
  const familles = [...new Set(connues.map((f) => f.famille).filter((n): n is string => !!n))];
  const annulees = connues.filter((f) => f.settlement === 'cancelled');

  const phrases: string[] = [`${eurosCourts(aPlacerCents)} à placer.`];

  if (connues.length === 0) {
    phrases.push(
      inconnues.length > 0
        ? 'Aucune des références citées ne désigne une facture de cette comptabilité.'
        : 'Aucun numéro de facture de cette comptabilité n’est cité.',
    );
  } else if (reclameCents > 0) {
    phrases.push(`Les factures citées encore dues en réclament ${eurosCourts(reclameCents)}.`);
  } else {
    phrases.push('Les factures citées sont toutes soldées : aucune ne réclame plus rien.');
  }

  if (connues.length > 0) {
    if (ecartCents > 0) {
      phrases.push(pourquoiLExcedent(ecartCents, notesCents, dejaAilleursCents));
    } else if (ecartCents < 0) {
      phrases.push(
        `Il manque ${eurosCourts(-ecartCents)} pour solder les factures citées : ce virement `
        + 'ne les couvre pas toutes.',
      );
    } else {
      phrases.push('Le compte tombe juste : ce reliquat solde exactement les factures citées.');
    }
  }

  if (familles.length > 1) {
    phrases.push(
      `Attention : les factures citées appartiennent à ${familles.length} familles `
      + `différentes — ${familles.join(', ')}.`,
    );
  }

  if (annulees.length > 0) {
    phrases.push(
      annulees.length === 1
        ? `${annulees[0].invoiceNumber} est annulée : elle ne réclame rien, même si son solde `
          + 'reste inscrit.'
        : `${annulees.length} factures citées sont annulées : elles ne réclament rien, même si `
          + 'leur solde reste inscrit.',
    );
  }

  if (inconnues.length > 0 && connues.length > 0) {
    phrases.push(
      inconnues.length === 1
        ? `La référence ${inconnues[0]} ne correspond à aucune facture de cette comptabilité.`
        : `${inconnues.length} références citées ne correspondent à aucune facture de cette `
          + `comptabilité : ${inconnues.join(', ')}.`,
    );
  }

  return {
    aPlacerCents, reclameCents, notesCents, dejaAilleursCents, ecartCents,
    inconnues, familles, phrases,
  };
}

/**
 * Pourquoi ce virement apporte plus que les factures citées ne réclament.
 *
 * ## Les deux causes, et pourquoi il fallait la seconde
 *
 * Une facture citée peut réclamer moins que son montant pour **deux** raisons,
 * et les deux se lisent de la même façon pour celui qui regarde : « le parent a
 * payé le prix affiché ».
 *
 * 1. **Une note de crédit** l'a réduite. Cas du 18 septembre 2026 au matin :
 *    60,00 € facturés, 30,00 € crédités, 30,00 € réclamés.
 * 2. **Un versement antérieur** l'avait déjà payée, en tout ou en partie. Cas
 *    du 18 septembre au soir, famille Ströbitzer : `CDV-WBK-2600179`, 158,00 €,
 *    avait reçu 35,00 € d'un virement de juin 2025 — le reliquat d'une facture
 *    réduite par une note de crédit, placé d'avance. Le parent a ensuite viré
 *    les 158,00 € entiers, sans déduire ces 35,00 €.
 *
 * La seconde manquait, et l'écran disait seulement « il resterait 35,00 €,
 * qu'aucune facture citée ne réclame » — vrai, et sans aucune valeur. La
 * réponse était pourtant dans le tableau : `DÉJÀ REÇU 158,00` en face de
 * `CE VIREMENT 123,00`. Il fallait faire la soustraction soi-même.
 *
 * ## Ce que cette fonction ne fait jamais
 *
 * **Affirmer une égalité fausse.** Quand les causes dépassent l'excédent — un
 * virement qui cite cinq vieilles factures soldées et paie tout autre chose —
 * elle les nomme sans prétendre qu'elles le chiffrent.
 */
function pourquoiLExcedent(
  ecartCents: number,
  notesCents: number,
  dejaAilleursCents: number,
): string {
  const causes: string[] = [];
  if (notesCents > 0) causes.push(`${eurosCourts(notesCents)} de notes de crédit`);
  if (dejaAilleursCents > 0) {
    causes.push(`${eurosCourts(dejaAilleursCents)} déjà reçus d’un versement antérieur`);
  }

  if (causes.length === 0) {
    return `Il resterait ${eurosCourts(ecartCents)}, qu’aucune facture citée ne réclame.`;
  }

  const explique = notesCents + dejaAilleursCents;
  const debut = `Il resterait ${eurosCourts(ecartCents)}`;

  if (explique === ecartCents) {
    // Le cas franc, et le seul où l'on peut dire « exactement ».
    if (dejaAilleursCents === 0) {
      return `${debut} — exactement ce que les notes de crédit ont retiré aux factures citées. `
        + 'Un parent qui a payé le montant d’origine verse ce montant en trop.';
    }
    if (notesCents === 0) {
      return `${debut} — exactement ce que les factures citées avaient déjà reçu d’un versement `
        + 'antérieur. Le parent a payé la facture entière, sans déduire ce qui y était déjà placé.';
    }
    return `${debut} — exactement ce que les factures citées portent déjà : ${causes.join(' et ')}.`;
  }

  if (explique < ecartCents) {
    return `${debut}. Les factures citées portent déjà ${causes.join(' et ')}, ce qui en `
      + 'explique une partie.';
  }

  /*
    Les causes dépassent l'excédent : on les nomme, on ne les chiffre pas
    contre lui. Une facture citée par erreur, soldée depuis des mois, gonfle
    ce total sans rien expliquer du virement d'aujourd'hui.
  */
  return `${debut}. Les factures citées portent déjà ${causes.join(' et ')} : le surplus vient `
    + 'de là, sans que les montants se recoupent exactement.';
}

/**
 * Les factures citées que ce virement pourrait encore solder, et pour combien.
 *
 * Construit la répartition **dans l'ordre où les factures ont été citées**,
 * chacune plafonnée à ce qu'elle réclame, l'ensemble plafonné à ce qu'il reste
 * à placer. Rien n'est écrit : l'écran montre ces lignes, et c'est un humain
 * qui décide de les appliquer.
 *
 * Les factures annulées en sont exclues — voir `reclamePar`.
 */
export function partsProposees(
  aPlacerCents: number,
  factures: FactureCitee[],
): { facture: FactureCitee; montantCents: number }[] {
  const parts: { facture: FactureCitee; montantCents: number }[] = [];
  let reste = aPlacerCents;
  for (const f of factures) {
    if (reste <= 0) break;
    if (!f.invoiceId) continue;
    const du = reclamePar(f);
    if (du <= 0) continue;
    const part = Math.min(reste, du);
    parts.push({ facture: f, montantCents: part });
    reste -= part;
  }
  return parts;
}

/* Le format court, pour des phrases. `money.ts` sert à l'affichage des tableaux. */
function eurosCourts(centimes: number): string {
  return `${(centimes / 100).toFixed(2).replace('.', ',')} €`;
}
